import cookies from 'js-cookie'
import { getActConfig } from '@/api/act'
import { RetainUsers } from '@/services/act/RetainUsers'
import { LifelongVipDiscount } from '@/services/act/LifelongVipDiscount'
import { countTimeDifference } from '@shared/utils/base/src/date'

/** 窗口需要用到的事件枚举 */
export const PopupEvent = {
  /** 终生会员活动 */
  lifelongVipDiscountEvent: 'lifelongVipDiscount-showPop',
  /** 挽留用户弹泡 */
  retainUsersEvent: 'retainUsers-showPop',
}

/** 用于各个弹窗控制器实例的枚举 */
export const PopupInsEnum = {
  retainUsers: 'retainUsers',
  lifelongVipDiscount: 'lifelongVipDiscount',
}

/** 缓存过期时间 单位天数 */
const expires = 100
const PopupCacheKey = 'PopupCacheKey'
const remoteConfigKey = 'quit_popup_config_pdf'
let isSecondClose = false

/**
 * 退弹窗口控制管理器
 *
 * 注意：此控制器类为单例，请不要在外部直接使用关键字New进行实例化，而是调用PopupController.getIns获取单例
 */
export class PopupController {
  /** @type {PopupController} */
  static _ins = null

  /** @type {string[]} */
  _config = null

  /** 每天限制的弹出限制次数 */
  limitPopupCount = 1

  /** 弹出的间隔天数 */
  limitPopupDay = 1

  /**
   * @type { { retainUsers: RetainUsers,lifelongVipDiscount: LifelongVipDiscount } }
   */
  _popUpCntMap = {
    retainUsers: null,
    lifelongVipDiscount: null,
  }

  /**
   * 获取弹窗控制器实例
   * @returns {PopupController}
   */
  static getIns () {
    if (!this._ins) {
      this._ins = new PopupController(true)
      this._ins._init()
    }
    return this._ins
  }

  constructor (isInnerCreated = false) {
    if (!isInnerCreated) {
      throw new Error('此类为单例，不能直接被实例化，请调用getIns方法获取实例')
    }
  }

  /**
   * 获取对应的弹窗控制器实例
   * @param { string } insKeyName
   * @returns { RetainUsers | }
   * */
  getControllerIns (insKeyName) {
    return this._popUpCntMap[insKeyName]
  }

  /**
   * 执行弹窗
   * @param {Object} vueIns vue 实例
   * @returns { Promise<boolean> } 是否弹窗的结果
   */
  async executePopup (vueIns) {
    if (!vueIns) {
      return false
    }

    if (this._isSencondClose()) {
      return false
    }

    this._updatePopupController(vueIns.$route, vueIns.$store)
    const eventName = await this._getValidEvent()
    if (eventName && vueIns.$EventBus) {
      vueIns.$EventBus.$emit(eventName)
      return true
    }
    return false
  }

  _isSencondClose () {
    if (isSecondClose) {
      return true
    }
    isSecondClose = true
    return false
  }

  /**
   * 标记已经弹出的缓存
   * @param {string} insEnumName
   */
  setPopUpCache (insEnumName) {
    /** @type {BasePopupScript} */
    const ins = this._popUpCntMap[insEnumName]

    if (!ins) {
      throw new Error('找不到对应控制器实例，请检查传入的参数是否正确')
    }

    ins.setHasShowTag()
    this._setPopUpCache()
  }

  _init () {
    this._popUpCntMap.retainUsers = new RetainUsers()
    this._popUpCntMap.lifelongVipDiscount = new LifelongVipDiscount()
  }

  /**
   * 获取允许启动对应弹窗的事件名
   * @returns { Promise<string | null> } 对应弹窗触发的事件,不触发则返回null,请进行判空处理
   */
  async _getValidEvent () {
    if (!this._config) {
      await this._updateRemoteConfig()
    }

    if (this._isPopupTimeLimited()) {
      return null
    }

    return await this._getPopupEvent()
  }

  /**
   * 更新各个需要更新的控制器
   * @param {Object} route 当前路由
   * @param {Object} store vuex仓库
   */
  _updatePopupController (route, store) {
    if (!route || !store) return

    const listName = Object.keys(this._popUpCntMap) || []
    listName.forEach((controllerName) => {
      this._popUpCntMap[controllerName].updateData(route, store)
    })
  }

  /**
   * 获取优惠券弹出决策结果
   * @returns { string | null }
   */
  async _getPopupEvent () {
    if (!Array.isArray(this._config)) {
      return null
    }

    for (const i of this._config) {
      const obj = this._popUpCntMap[i]
      if (!obj) {
        continue
      }

      const result = await obj.valid()
      if (result) {
        return obj.getEventName()
      }
    }

    return null
  }

  /**
   * 弹出次数是否被限制
   * @returns {boolean}
   */
  _isPopupTimeLimited () {
    const cacheObj = this._getPopUpCache()
    // 没有缓存表示没有弹过
    if (!cacheObj) {
      return false
    }

    const { popup = 0, timeStamp = 0 } = cacheObj
    const parseNum = parseInt(timeStamp)
    const oldTime = isNaN(parseNum) ? 0 : parseNum
    const { days } = countTimeDifference(new Date(oldTime))

    if (days >= this.limitPopupDay) {
      return false
    }

    if (popup < this.limitPopupCount) {
      return false
    }
    return true
  }

  /**
   * 获取缓存对象
   * @returns { { popup: number, timeStamp: number } | null }
   */
  _getPopUpCache () {
    const cache = cookies.get(PopupCacheKey)
    let cacheObj = null
    if (cache) {
      try {
        cacheObj = JSON.parse(cache)
      } catch (err) {
        console.error(JSON.stringify(err))
      }
    }
    return cacheObj
  }

  _setPopUpCache () {
    const cacheObj = this._getPopUpCache() || { popup: 0 }
    cacheObj.popup += 1
    cacheObj.timeStamp = Date.now()
    cookies.set(PopupCacheKey, JSON.stringify(cacheObj), { expires })
  }

  /**
   * 更新远程配置信息
   */
  async _updateRemoteConfig () {
    const res = await getActConfig({ type: remoteConfigKey })

    if (res && res.resp_common && res.resp_common.ret === 0) {
      const actData = res[remoteConfigKey] || {}
      this._config = actData.list || []
      this.limitPopupCount = actData.popup || this.limitPopupCount
    }
  }
}
